import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  type KibMultiSelectionOption,
  KibSuperSelect,
  type KibSuperSelectProps,
} from '@chewy/kib-fields-new-react'
import { useDebounce } from 'use-debounce'
import { Defaults } from '@pbt/pbt-ui-components'

import {
  useGetRoleIdsList,
  UserSelectFilterScope,
} from '~/components/common/inputs/UserSelect'
import SecretSearchContext from '~/constants/secretSearchContext'
import {
  clearDynamicSearchCache,
  fetchSuggestionResults,
} from '~/store/actions/search'
import { getStateSpecificSuggestionResults } from '~/store/reducers/search'
import { getUsersMap } from '~/store/reducers/users'
import { type SuggestionResult, TeamFilter } from '~/types'
import useEffectExceptOnMount from '~/utils/useEffectExceptOnMount'

import '@chewy/kib-fields-new-styles/dist/kib-fields-new.css'
import '@chewy/kib-interstitials-styles/dist/styles/kib-spinner.css'

export interface KibUserSelectProps {
  disabled?: boolean
  filterScope?: UserSelectFilterScope
  includeInactive?: boolean
  label?: string
  onChange?: (e: string) => void
  placeHolder: string
  readOnly?: boolean
  required?: boolean
  /**
   * When true, onChange is NOT called when the selection is cleared.
   * Useful when clearing should only reset UI without triggering side effects.
   * @default false
   */
  reset?: boolean
  teamFilter?: TeamFilter
  useInMemory?: boolean
  userSelectStateId: string
  value?: string
}

export const KibUserSelect = ({
  reset = false,
  label,
  userSelectStateId,
  includeInactive = false,
  required = false,
  teamFilter = TeamFilter.MEMBER,
  placeHolder,
  disabled,
  onChange,
  value,
  readOnly,
  filterScope = UserSelectFilterScope.MainStaff,
  useInMemory,
}: KibUserSelectProps) => {
  const dispatch = useDispatch()
  const roleIdsList = useGetRoleIdsList(filterScope)
  const searchResult = useSelector(
    getStateSpecificSuggestionResults(userSelectStateId),
  )
  const isLoading = searchResult.isSuggestionReceiving
  const searchResults: SuggestionResult[] =
    searchResult.suggestionResults as SuggestionResult[]
  const usersMap = useSelector(getUsersMap)
  const maxCount = useInMemory ? 50 : undefined
  const [userOptions, setUserOptions] = useState<KibMultiSelectionOption[]>([])
  const [internalValue, setInternalValue] = useState<KibMultiSelectionOption[]>(
    [],
  )
  const [rawInputValue, setRawInputValue] = useState('')
  const [inputValue] = useDebounce(rawInputValue, Defaults.DEBOUNCE_ACTION_TIME)

  const dispatchFetchSearch = (searchTerm: string) => {
    dispatch(
      fetchSuggestionResults({
        searchContext: SecretSearchContext.PERSONS,
        searchTerm,
        teamFilter,
        roleIds: roleIdsList.join(','),
        includeInactive,
        maxCount,
        shorten: true,
        stateId: userSelectStateId,
      }),
    )
  }

  useEffect(
    () => () => {
      dispatch(clearDynamicSearchCache(userSelectStateId))
    },
    [],
  )

  useEffect(() => {
    if (value && !inputValue) {
      const userOption = {
        label: `${usersMap[value]?.firstName || ''} ${
          usersMap[value]?.lastName || ''
        }`,
        value,
      }

      setInternalValue([userOption])
      setUserOptions([userOption])
    }
  }, [value])

  useEffect(() => {
    if (searchResults && inputValue) {
      const userOptionsResult = searchResults.map((result) => ({
        label: result.item ?? '',
        value: result.id,
      }))
      setUserOptions(userOptionsResult)
    }
  }, [searchResults, inputValue])

  useEffectExceptOnMount(() => {
    if (inputValue) {
      dispatchFetchSearch(inputValue)
    }
  }, [inputValue])

  const setValues = (
    values: KibMultiSelectionOption[],
    ev: Parameters<NonNullable<KibSuperSelectProps['onChange']>>[1],
  ) => {
    if (ev?.type === 'blur') {
      return
    }

    const users = values.map((o) => usersMap[o.value]).filter(Boolean)

    if (users.length === 1) {
      if (onChange) {
        onChange(users[0].id)
      }
      setInternalValue(values)
    }
  }

  const onClear = () => {
    if (!reset) {
      onChange?.('')
    }
    setInternalValue([])
  }

  const onQuery = (
    v?: string,
    e?:
      | React.ChangeEvent<HTMLInputElement>
      | React.MouseEvent<HTMLElement>
      | React.FocusEvent<HTMLInputElement>
      | React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e?.type === 'change' && v && v.length) {
      setRawInputValue(v)
    }
  }

  return (
    <KibSuperSelect
      isAsync
      disabled={disabled || readOnly}
      label={label || ''}
      loading={isLoading}
      options={userOptions}
      placeholder={placeHolder}
      required={required}
      value={internalValue}
      onChange={(data, event) => setValues(data, event)}
      onClear={onClear}
      onQuery={onQuery}
    />
  )
}
